import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IActivityLog, IComplaint } from "../../exports/interfaces";
import useMobile from "../../hooks/useMobile";
import Select from "react-select";
import { customSelectStyles } from "../../exports/customSelectStyles";
import { Pencil } from "lucide-react";
import DocumentPopUp from "../DocumentPopUp";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useLoader } from "../../context/LoaderContext";
import { usePermission } from "../../hooks/usePermission";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { fetchComplaintById } from "../../api/ComplaintApi";
import { useOption } from "../../context/OptionContext";
import { darkenColor, formatDateBySetting } from "../../exports/functions";
import { useSettings } from "../../context/SettingsContext";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

const ViewComplaints = ({
  complaint,
  setComplaint,
}: {
  complaint: IComplaint | null;
  setComplaint: Dispatch<SetStateAction<IComplaint | null>>;
}) => {
  const { statusOptions, categories } = useOption();
  const { generalSettings } = useSettings();
  const { user } = useUser();
  const { sendNotification } = useNotification();
  const { setLoading } = useLoader();
  const { canEdit } = usePermission();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [showDoc, setShowDoc] = useState(false);
  const [editingLogId, setEditingLogId] = useState<number | null>(null);
  const [editingDescription, setEditingDescription] = useState("");
  const [noteError, setNoteError] = useState("");
  const [statusError, setStatusError] = useState("");
  const [editingError, setEditingError] = useState("");
  const isSolved = complaint?.status?.toLowerCase() === "solved";

  const handleEditClick = (log: IActivityLog) => {
    setEditingLogId(log.id);
    setEditingDescription(log.description);
    setEditingError("");
  };

  const statusComplaints =
    statusOptions
      ?.find((item) => item.name === "Complaint Statuses")
      ?.statusOptions?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Complaint Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const priorityStyle = (priority: string) => {
    const priorityOptions =
      categories?.find((item) => item.name === "Complaint Priority")
        ?.dropdowns || [];

    const matchedPriority = priorityOptions.find(
      (item) => item.label?.toLowerCase() === priority?.toLowerCase(),
    );

    return {
      backgroundColor: matchedPriority?.color || "#ccc",
      color: darkenColor(matchedPriority?.color || "#ccc", 40),
    };
  };

  useEffect(() => {
    setStatus(complaint?.status || "");
  }, [complaint?.status]);

  const scrollToFirstError = (field: "note" | "status") => {
    const element =
      field === "note"
        ? (document.querySelector(
            '[data-field="activity-note"]',
          ) as HTMLElement | null)
        : (document.querySelector(
            '[data-field="complaint-status"]',
          ) as HTMLElement | null);

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // element.focus?.();
    }
  };

  const handleAddActivityLog = async () => {
    if (isSolved) return;

    if (!note.trim()) {
      setNoteError("Activity note is required.");
      scrollToFirstError("note");
      return;
    }

    setNoteError("");
    setLoading(true);

    try {
      const response = await axiosClient.post(
        `/complaints/activity-logs/${complaint?.id}`,
        {
          description: note.trim(),
        },
      );

      if (response.status === 200 || response.status === 201) {
        fetchComplaintById(complaint?.id || 0)
          .then((complaintData) => {
            setComplaint(complaintData);
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || "Something went wrong!";
            dynamicFailed(message);
          });
        setNote("");
        sendNotification(`Activity log added`, user?.user?.userName || "User");
        dynamicSuccess("Activity log added successfully!");
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleSaveActivityLog = async (id: number) => {
    if (isSolved) return;

    if (!editingDescription.trim()) {
      setEditingError("Activity note is required.");
      return;
    }

    setEditingError("");
    try {
      setLoading(true);

      const response = await axiosClient.put(
        `/complaints/activity-logs/${id}`,
        {
          description: editingDescription.trim(),
        },
      );

      if (response.status === 200 || response.status === 201) {
        fetchComplaintById(complaint?.id || 0)
          .then((complaintData) => {
            setComplaint(complaintData);
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || "Something went wrong!";
            dynamicFailed(message);
          });
        setEditingLogId(null);
        setEditingDescription("");
        sendNotification(
          `Activity log updated`,
          user?.user?.userName || "User",
        );
        dynamicSuccess("Activity log updated successfully!");
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleUpdate = async () => {
    if (isSolved) return;

    if (!status.trim()) {
      setStatusError("Please select a complaint status.");
      scrollToFirstError("status");
      return;
    }

    setStatusError("");
    setLoading(true);

    try {
      const payload = new FormData();

      payload.append("status", status);

      const response = await axiosClient.put(
        `/complaints/${complaint?.id}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.status === 200 || response.status === 201) {
        // Build status history chain from existing status update logs
        const statusLogs = (complaint?.activityLogs || [])
          .filter((log) => log.statusUpdate)
          .sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
        let description = "";
        if (statusLogs.length === 0) {
          // First status update — just the new status
          description = status;
        } else {
          // Get the last status log's description and append the new status
          const lastLog = statusLogs[statusLogs.length - 1];
          description = `${lastLog.description} → ${status}`;
        }

        // Post activity log for status change
        await axiosClient.post(`/complaints/activity-logs/${complaint?.id}`, {
          description,
          statusUpdate: true,
        });

        fetchComplaintById(complaint?.id || 0)
          .then((complaintData) => {
            setComplaint(complaintData);
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || "Something went wrong!";
            dynamicFailed(message);
          });
        sendNotification(`Complaint updated`, user?.user?.userName || "User");
        dynamicSuccess("Complaint updated successfully!");
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Complaint Detail</h3>
          <small>
            Record a new complaint from a cooperative member or stakeholder
          </small>
        </div>
      </div>
      <div className="dashboard-card p-3">
        {/* Header */}
        <div className="dashboard-card">
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-end gap-2 flex-wrap">
              <div>
                <h5 className="mb-1">CMP-2026-{complaint?.id}</h5>

                <small className="d-block text-muted">
                  {complaint?.complaintType}
                </small>
              </div>

              <div className="mt-2 d-flex align-items-end gap-2">
                <span
                  className={`badge rounded-pill px-3 py-2`}
                  style={statusStyle(complaint?.status || "")}
                >
                  {complaint?.status}
                </span>

                <span
                  className="badge rounded-pill px-3 py-2"
                  style={priorityStyle(complaint?.complaintPriority || "")}
                >
                  {complaint?.complaintPriority}
                </span>
              </div>
            </div>

            {canEdit("complaints") && (
              <button
                className="btn btn-badge fs-small"
                onClick={() => navigate(`/complaints/edit/${complaint?.id}`)}
                disabled={isSolved}
              >
                <i className={`${!isMobile ? "me-2" : ""}`}>
                  <Pencil size={15} />
                </i>
                {!isMobile && "Edit"}
              </button>
            )}
          </div>

          <hr />

          <h6 className="primary-color mb-3">Complaint Information</h6>

          <div className="row g-3">
            <Info
              label="Complaint Name"
              value={complaint?.complainantName || "-"}
            />

            <Info label="Mobile" value={complaint?.complainantContact || "-"} />

            <Info label="Email Address" value={complaint?.email || "-"} />

            <Info
              label="Received Date"
              value={formatDateBySetting(
                complaint?.filedOn,
                generalSettings?.dateFormat,
              )}
            />
          </div>

          <hr />

          <h6 className="primary-color mb-3">Complaint Details</h6>

          <div className="row g-3">
            <Info
              label="Complaint Type"
              value={complaint?.complaintType || "-"}
            />

            <Info
              label="Complaint Priority"
              value={complaint?.complaintPriority || "-"}
            />

            <Info
              label="Complaint Area"
              value={complaint?.complaintArea || "-"}
            />

            <Info
              label="Cooperative Name"
              value={complaint?.cooperativeName || "-"}
            />

            <Info
              label="Cooperative Registration"
              value={complaint?.cooperativeRegistrationNumber || "-"}
            />

            <div className="col-md-8 details-text">
              <small className="d-block">Complaint Detail</small>

              <p>{complaint?.complainDetails}</p>
            </div>

            <div className="col-md-4 text-end">
              <button
                className="btn btn-badge-border px-4"
                onClick={() => setShowDoc(true)}
              >
                View Document
              </button>
            </div>
          </div>
        </div>
        <div className="row mt-1 g-3">
          <div className="col-lg-8">
            <div className="row">
              <div className="col-12">
                <div className="dashboard-card">
                  <h6 className="primary-color mb-3">Add Activity Note</h6>

                  <div data-field="activity-note">
                    <textarea
                      className={`form-control ${noteError ? "input-error" : ""}`}
                      rows={4}
                      placeholder="Describe the action taken"
                      value={note}
                      disabled={isSolved}
                      onChange={(e) => {
                        setNote(e.target.value);
                        // if (noteError) setNoteError("");
                      }}
                    />
                    {noteError && <p className="error-message">{noteError}</p>}
                  </div>

                  <div className="text-end mt-3">
                    <button
                      className="btn btn-badge-border"
                      onClick={handleAddActivityLog}
                      disabled={isSolved}
                    >
                      <i className="fa-solid fa-plus"></i> Add Activity Log
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-12 mt-3">
                <div className="dashboard-card p-3">
                  <h6 className="primary-color mb-3">Activity Log</h6>

                  {complaint?.activityLogs?.length ? (
                    [...(complaint?.activityLogs || [])]
                      .sort(
                        (a, b) =>
                          new Date(b.createdAt).getTime() -
                          new Date(a.createdAt).getTime(),
                      )
                      .map((log) => (
                        <div
                          className="activity-item d-flex gap-2 align-items-start mb-3"
                          key={log.id}
                        >
                          <div className="activity-left">
                            {!log.statusUpdate ? (
                              <div
                                className="activity-note-icon cursor-pointer"
                                aria-disabled={isSolved}
                                style={{
                                  pointerEvents: isSolved ? "none" : "auto",
                                  opacity: isSolved ? 0.5 : 1,
                                }}
                                onClick={() =>
                                  editingLogId === log.id
                                    ? handleSaveActivityLog(log.id)
                                    : handleEditClick(log)
                                }
                              >
                                {editingLogId === log.id ? (
                                  <i className="fa-solid fa-floppy-disk text-success"></i>
                                ) : (
                                  <Pencil size={14} />
                                )}
                              </div>
                            ) : (
                              <div
                                className="activity-note-icon"
                                aria-disabled={isSolved}
                                style={{
                                  pointerEvents: isSolved ? "none" : "auto",
                                  opacity: isSolved ? 0.5 : 1,
                                }}
                              >
                                <i className="fa-solid fa-arrows-rotate"></i>
                              </div>
                            )}
                          </div>
                          <div className="activity-note-card w-100">
                            <div className="d-flex gap-2 align-items-start">
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h6 className="mb-0 activity-note-title">
                                    {log.statusUpdate
                                      ? "Status Update"
                                      : "Activity Note"}
                                  </h6>

                                  <small className="text-muted">
                                    {new Date(log.createdAt).toLocaleString(
                                      "en-GB",
                                      {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: true,
                                      },
                                    )}
                                  </small>
                                </div>

                                {editingLogId === log.id ? (
                                  <>
                                    <textarea
                                      className={`form-control ${
                                        editingError ? "input-error" : ""
                                      }`}
                                      rows={3}
                                      value={editingDescription}
                                      disabled={isSolved}
                                      onChange={(e) => {
                                        setEditingDescription(e.target.value);

                                        // if (editingError) {
                                        //   setEditingError("");
                                        // }
                                      }}
                                    />

                                    {editingError && (
                                      <p className="error-message">
                                        {editingError}
                                      </p>
                                    )}
                                  </>
                                ) : (
                                  <p
                                    className="mb-0 activity-note-description"
                                    style={
                                      log.statusUpdate
                                        ? { color: "var(--primary-color)" }
                                        : {}
                                    }
                                  >
                                    {log.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                  ) : (
                    <p className="text-muted mb-0">No Activity yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="dashboard-card">
              <div className="dashboard-card p-3">
                <h6 className="primary-color mb-3">Update Status</h6>

                <div className="mb-3">
                  <label className="form-label">Complaint Status</label>

                  <div>
                    <span
                      className={`badge rounded-pill px-3 py-2`}
                      style={statusStyle(complaint?.status || "")}
                    >
                      {complaint?.status}
                    </span>
                  </div>
                </div>

                <div className="mb-3" data-field="complaint-status">
                  <label className="form-label">Change To</label>
                  <Select
                    styles={customSelectStyles}
                    options={statusComplaints}
                    value={statusComplaints.find((o) => o.value === status)}
                    isDisabled={isSolved}
                    onChange={(selected) => {
                      setStatus(selected?.value || "");
                      if (statusError) setStatusError("");
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {statusError && (
                    <p className="error-message">{statusError}</p>
                  )}
                </div>

                <button
                  className="btn btn-badge-border w-100"
                  onClick={handleUpdate}
                  disabled={isSolved}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DocumentPopUp
        showDoc={showDoc}
        setShowDoc={setShowDoc}
        docs={complaint?.documentUrl}
      />
    </>
  );
};

export const Info = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div className="col-md-4 details-text">
    <small className="d-block">{label}</small>
    <p>{value}</p>
  </div>
);

export default ViewComplaints;
